/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { Column, Bar } from '@ant-design/plots';

import {
  ChartCard,
  FilterDropdown,
  PosterWall,
  SectionCard,
  StatCard,
} from '../../../ui';
import {
  boardMemberClockIn,
  boardMemberPhotoWallPage,
} from '@/services/athenaBoard';
import styles from '../board.less';

/**
 * 5. 会员打卡
 * ------------------------------------------------------------------
 * 自取数（不做容器预取）：维度切换要重新请求，放在本 pane 里才能配合
 * 外层 LazyMount —— 滚到视口才发请求。
 */
export const ClockInPane = ({ tenantId, coachUserId, enums }) => {
  const [dimension, setDimension] = useState('day');
  const [clockIn, setClockIn] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!tenantId || !coachUserId) return;
    setLoading(true);
    setError('');
    boardMemberClockIn({ tenantId, coachUserId, dimension })
      .then((r) => {
        if (r && r.code === '00000') setClockIn(r.data || {});
        else throw new Error((r && r.msg) || '会员打卡加载失败');
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [tenantId, dimension, coachUserId, retryKey]);

  /* 打卡照片墙：2026-09-20 从 /memberClockIn 抽成独立分页接口
     （后端同步拆出 /athena/board/memberPhotoWallPage）—— 统计随维度切换重新取数，
     照片墙是无限滚动，合在一起会让每次切日/周/月都白带一页照片。 */
  const fetchPhotoWall = useCallback(
    ({ lastId, limit }) =>
      boardMemberPhotoWallPage({ tenantId, coachUserId, lastId, limit }).then(
        (r) => {
          if (!r || r.code !== '00000') {
            throw new Error((r && r.msg) || '打卡照片墙加载失败');
          }
          return r.data;
        },
      ),
    [tenantId, coachUserId],
  );

  const summary = clockIn.summary || {};
  const trend = clockIn.trend || {};

  return (
    <>
      <SectionCard
        title="会员打卡"
        loading={loading}
        error={error}
        onRetry={() => setRetryKey((current) => current + 1)}
        extra={
          <FilterDropdown
            label="趋势"
            value={dimension}
            options={enums.clockDimension || []}
            onChange={setDimension}
          />
        }
      >
        <div className={styles.kpiRow}>
          <StatCard
            title="累计打卡人数"
            value={summary.totalClockUserNum}
            unit="人"
          />
          <StatCard
            title="本周打卡人数"
            value={summary.weekClockUserNum}
            unit="人"
          />
          <StatCard
            title="本月打卡人数"
            value={summary.monthClockUserNum}
            unit="人"
          />
          <StatCard
            title="周人均打卡次数"
            value={summary.weekPerUserTimes}
            precision={1}
            unit="次"
          />
        </div>
        <ChartCard height={300} empty={!(trend.labels || []).length}>
          <Column
            height={300}
            data={(trend.labels || []).map((label, i) => ({
              label,
              value: (trend.total || [])[i] || 0,
            }))}
            xField="label"
            yField="value"
            legend={false}
            xAxis={{ label: { autoHide: true } }}
          />
        </ChartCard>
      </SectionCard>

      <div className={styles.chartRow}>
        <SectionCard title="打卡类型分布">
          <ChartCard
            height={260}
            empty={!(clockIn.typeDistribution || []).length}
          >
            <Bar
              height={260}
              data={clockIn.typeDistribution || []}
              xField="value"
              yField="name"
              legend={false}
            />
          </ChartCard>
        </SectionCard>
        <SectionCard title="习惯指数分布">
          <ChartCard
            height={260}
            empty={!(clockIn.habitLevelDistribution || []).length}
          >
            <Bar
              height={260}
              data={clockIn.habitLevelDistribution || []}
              xField="value"
              yField="name"
              legend={false}
            />
          </ChartCard>
        </SectionCard>
      </div>

      {/* 契约变更：源不下发总数 → 不再展示「共 N 张」，只用 hasMore 判断续拉（G7） */}
      <SectionCard title="打卡照片墙" subtitle="横向滑动预览 · 滚动加载更多">
        <PosterWall
          fetchPage={fetchPhotoWall}
          resetKey={`${tenantId}-${coachUserId}`}
          emptyDescription="该教练暂无打卡照片"
          emptyHint="会员完成打卡并上传照片后可在此查看"
        />
      </SectionCard>
    </>
  );
};

export default ClockInPane;
